import { supabase, isSupabaseConfigured } from "../supabase";

/**
 * Transactions API helpers using Supabase
 */

/** Fallback názov balíka z metadata keď package bola zmazaná (package_id → NULL) */
export function getTransactionPackageName(
  t: Pick<Transaction, "package" | "metadata">,
): string {
  if (t.package?.name) return t.package.name;
  const meta = t.metadata as { package_name?: string } | null | undefined;
  return meta?.package_name && typeof meta.package_name === "string"
    ? meta.package_name
    : "-";
}

export interface Transaction {
  id: number;
  user_id: string | null;
  pair_id: number | null;
  package_id: number | null;
  transaction_type: "purchase" | "use" | "refund";
  hours_delta: number;
  amount: number;
  balance_after: number | null;
  reference: string | null;
  metadata: Record<string, unknown> | null;
  is_paid: boolean;
  occurred_at: string;
  created_at: string;
  // Joined data (optional)
  package?: {
    id: number;
    name: string;
    package_type: string;
  };
  user?: {
    id: string;
    email: string;
    display_name: string | null;
  };
}

export const transactionsApi = {
  /**
   * Fetch transactions with filters
   */
  async list(filters?: {
    user_id?: string;
    pair_id?: number;
    package_id?: number;
    transaction_type?: Transaction["transaction_type"];
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; data?: Transaction[]; error?: string }> {
    try {
      let query = supabase.from("transactions").select(`
          *,
          package:packages(id, name, package_type)
        `);

      if (filters?.user_id) {
        query = query.eq("user_id", filters.user_id);
      }

      if (filters?.pair_id) {
        query = query.eq("pair_id", filters.pair_id);
      }

      if (filters?.package_id) {
        query = query.eq("package_id", filters.package_id);
      }

      if (filters?.transaction_type) {
        query = query.eq("transaction_type", filters.transaction_type);
      }

      query = query.order("occurred_at", { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      if (filters?.offset) {
        query = query.range(
          filters.offset,
          filters.offset + (filters.limit || 50) - 1,
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      // Načítať users údaje samostatne, pretože foreign key smeruje na auth.users
      // ale data sú v public.users
      if (data && data.length > 0) {
        const userIds = [
          ...new Set(data.map((t) => t.user_id).filter(Boolean)),
        ] as string[];

        // Pre use/refund bez start_at v metadata načítať termín z private_lessons
        const lessonIdsToFetch = data
          .filter(
            (t) =>
              (t.transaction_type === "use" ||
                t.transaction_type === "refund") &&
              t.metadata &&
              typeof t.metadata === "object" &&
              "private_lesson_id" in t.metadata &&
              typeof (t.metadata as { private_lesson_id?: number })
                .private_lesson_id === "number" &&
              !("start_at" in t.metadata),
          )
          .map(
            (t) =>
              (t.metadata as { private_lesson_id: number }).private_lesson_id,
          );
        const uniqueLessonIds = [...new Set(lessonIdsToFetch)];

        let lessonsMap: Map<number, { start_at: string; status: string }> =
          new Map();
        if (uniqueLessonIds.length > 0) {
          const { data: lessons } = await supabase
            .from("private_lessons")
            .select("id, start_at, status")
            .in("id", uniqueLessonIds);
          if (lessons) {
            lessonsMap = new Map(
              lessons.map((l) => [
                l.id,
                { start_at: l.start_at, status: l.status ?? "" },
              ]),
            );
          }
        }

        const transactionsWithUsersAndSlot = data
          .map((transaction) => {
            let enrichedMetadata = transaction.metadata;
            if (
              (transaction.transaction_type === "use" ||
                transaction.transaction_type === "refund") &&
              transaction.metadata &&
              typeof transaction.metadata === "object" &&
              !("start_at" in transaction.metadata)
            ) {
              const lessonId = (
                transaction.metadata as { private_lesson_id?: number }
              ).private_lesson_id;
              if (typeof lessonId === "number") {
                const lesson = lessonsMap.get(lessonId);
                if (lesson) {
                  enrichedMetadata = {
                    ...transaction.metadata,
                    start_at: lesson.start_at,
                  };
                }
              }
            }
            return {
              ...transaction,
              metadata: enrichedMetadata,
            };
          })
          .filter((transaction) => {
            // V histórii zobrazovať len "Použitie" pre lekcie so statusom completed (nie schválené)
            if (transaction.transaction_type !== "use") return true;
            const meta = transaction.metadata as
              | { private_lesson_id?: number }
              | null
              | undefined;
            const lessonId =
              meta &&
              typeof meta === "object" &&
              typeof meta.private_lesson_id === "number"
                ? meta.private_lesson_id
                : null;
            if (lessonId === null) return true;
            const lesson = lessonsMap.get(lessonId);
            if (!lesson) return true; // lekcia zmazaná alebo neznáma – zobraziť
            return lesson.status === "completed";
          });

        if (userIds.length > 0) {
          const { data: usersData } = await supabase
            .from("users")
            .select("id, email, display_name")
            .in("id", userIds);

          const transactionsWithUsers = transactionsWithUsersAndSlot.map(
            (transaction) => {
              const userData = usersData?.find(
                (u) => u.id === transaction.user_id,
              );
              return {
                ...transaction,
                user: userData || undefined,
              };
            },
          );

          return {
            success: true,
            data: transactionsWithUsers as Transaction[],
          };
        }

        return {
          success: true,
          data: transactionsWithUsersAndSlot as Transaction[],
        };
      }

      return { success: true, data: data as Transaction[] };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch transactions",
      };
    }
  },

  /**
   * Fetch a single transaction by ID
   */
  async get(
    id: number,
  ): Promise<{ success: boolean; data?: Transaction; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("transactions")
        .select(
          `
          *,
          package:packages(id, name, package_type)
        `,
        )
        .eq("id", id)
        .single();

      if (error) throw error;

      // Načítať user údaje samostatne
      if (data && data.user_id) {
        const { data: userData } = await supabase
          .from("users")
          .select("id, email, display_name")
          .eq("id", data.user_id)
          .single();

        return {
          success: true,
          data: {
            ...data,
            user: userData || undefined,
          } as Transaction,
        };
      }

      return { success: true, data: data as Transaction };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch transaction",
      };
    }
  },

  /**
   * Create a new transaction (usually done by backend/Edge Function)
   */
  async create(payload: {
    user_id?: string;
    pair_id?: number;
    package_id?: number;
    transaction_type: Transaction["transaction_type"];
    hours_delta: number;
    amount: number;
    balance_after?: number;
    reference?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ success: boolean; data?: Transaction; error?: string }> {
    try {
      if (!isSupabaseConfigured) {
        return {
          success: false,
          error:
            "Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env file",
        };
      }

      const transactionData = {
        ...payload,
        occurred_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("transactions")
        .insert(transactionData)
        .select()
        .single();

      if (error) {
        console.error("Transaction insert error:", error);
        throw error;
      }

      if (!data) {
        throw new Error("Transaction was not created - no data returned");
      }

      return { success: true, data: data as Transaction };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create transaction";

      console.error("Transaction creation failed:", errorMessage, error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Update transaction payment status
   */
  async updatePaymentStatus(
    id: number,
    is_paid: boolean,
  ): Promise<{ success: boolean; data?: Transaction; error?: string }> {
    try {
      if (!isSupabaseConfigured) {
        return {
          success: false,
          error: "Supabase is not configured",
        };
      }

      const { data, error } = await supabase
        .from("transactions")
        .update({ is_paid })
        .eq("id", id)
        .select(
          `
          *,
          package:packages(id, name, package_type)
        `,
        )
        .single();

      if (error) throw error;

      // Načítať user údaje samostatne
      if (data && data.user_id) {
        const { data: userData } = await supabase
          .from("users")
          .select("id, email, display_name")
          .eq("id", data.user_id)
          .single();

        return {
          success: true,
          data: {
            ...data,
            user: userData || undefined,
          } as Transaction,
        };
      }

      return { success: true, data: data as Transaction };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update payment status",
      };
    }
  },
};
